import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import { useEffect, useState } from 'react';
import axios from 'axios';
import { useDispatch, useSelector } from 'react-redux';
import PriceSidebar from './PriceSidebar';
import Stepper from './Stepper';
import { useSnackbar } from 'notistack';
import { saveShippingInfo } from '../../actions/cartAction';
import { useNavigate } from 'react-router-dom';
import MetaData from '../Layouts/MetaData';
import useCartPricing from '../../utils/useCartPricing';
import BackdropLoader from '../Layouts/BackdropLoader';
import { useLang, uiText } from '../../utils/i18n';

const emptyForm = { address: "", region: "", areaId: "", phoneNo: "", phoneCountryCode: "852" };

const COUNTRY_CODES = [
    { code: "852", label: "🇭🇰 +852 (Hong Kong)" },
    { code: "91", label: "🇮🇳 +91 (India)" },
    { code: "86", label: "🇨🇳 +86 (China)" },
    { code: "65", label: "🇸🇬 +65 (Singapore)" },
    { code: "60", label: "🇲🇾 +60 (Malaysia)" },
    { code: "44", label: "🇬🇧 +44 (UK)" },
    { code: "1", label: "🇺🇸 +1 (US/Canada)" },
    { code: "61", label: "🇦🇺 +61 (Australia)" },
    { code: "81", label: "🇯🇵 +81 (Japan)" },
    { code: "82", label: "🇰🇷 +82 (South Korea)" },
    { code: "971", label: "🇦🇪 +971 (UAE)" },
    { code: "92", label: "🇵🇰 +92 (Pakistan)" },
    { code: "880", label: "🇧🇩 +880 (Bangladesh)" },
    { code: "63", label: "🇵🇭 +63 (Philippines)" },
    { code: "62", label: "🇮🇩 +62 (Indonesia)" },
];

const Shipping = () => {

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();
    const { lang } = useLang();

    const { cartItems } = useSelector((state) => state.cart);
    const pricing = useCartPricing(cartItems);

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [addresses, setAddresses] = useState([]);
    const [selectedId, setSelectedId] = useState("");
    const [showForm, setShowForm] = useState(false);
    const [saveThisAddress, setSaveThisAddress] = useState(true);
    const [form, setForm] = useState(emptyForm);

    const [shippingAreas, setShippingAreas] = useState([]);

    useEffect(() => {
        const fetchAll = async () => {
            try {
                const [addressesRes, areasRes] = await Promise.all([
                    axios.get('/api/v1/addresses'),
                    axios.get('/api/v1/shipping-areas'),
                ]);
                setAddresses(addressesRes.data.addresses);
                setShippingAreas(areasRes.data.areas);
                if (addressesRes.data.addresses.length > 0) {
                    const defaultAddr = addressesRes.data.addresses.find((a) => a.isDefault) || addressesRes.data.addresses[0];
                    setSelectedId(defaultAddr._id);
                } else {
                    setShowForm(true);
                }
            } catch (error) {
                console.error('Failed to load addresses/shipping areas', error);
                setShowForm(true);
            } finally {
                setLoading(false);
            }
        };
        fetchAll();
    }, []);

    const regions = [...new Set(shippingAreas.map((a) => a.region))];
    const areasForRegion = shippingAreas.filter((a) => a.region === form.region);
    const selectedArea = shippingAreas.find((a) => a._id === form.areaId);

    const shippingSubmit = async (e) => {
        e.preventDefault();

        if (!showForm && selectedId) {
            const addr = addresses.find((a) => a._id === selectedId);
            dispatch(saveShippingInfo({ address: addr.address, city: addr.area, state: addr.region, country: addr.country || "HK", pincode: addr.pincode, phoneNo: addr.phoneNo, phoneCountryCode: addr.phoneCountryCode || "852", areaId: addr.areaId }));
            navigate("/order/confirm");
            return;
        }

        if (form.phoneCountryCode === "852" && form.phoneNo.length !== 8) {
            enqueueSnackbar(uiText('hkPhoneDigits', lang), { variant: "error" });
            return;
        }
        if (form.phoneCountryCode !== "852" && (form.phoneNo.length < 6 || form.phoneNo.length > 15)) {
            enqueueSnackbar(uiText('validPhoneNumber', lang), { variant: "error" });
            return;
        }
        if (!form.areaId) {
            enqueueSnackbar(uiText('pleaseChooseDeliveryArea', lang), { variant: "error" });
            return;
        }

        setSaving(true);
        try {
            const payload = {
                address: form.address,
                region: form.region,
                area: selectedArea?.name,
                areaId: form.areaId,
                phoneNo: form.phoneNo,
                phoneCountryCode: form.phoneCountryCode,
                country: "HK",
            };
            if (saveThisAddress) {
                await axios.post('/api/v1/address/new', { ...payload, isDefault: addresses.length === 0 });
            }
            dispatch(saveShippingInfo({ address: form.address, city: selectedArea?.name, state: form.region, country: "HK", areaId: form.areaId, phoneNo: form.phoneNo, phoneCountryCode: form.phoneCountryCode }));
            navigate("/order/confirm");
        } catch (error) {
            enqueueSnackbar(error?.response?.data?.message || uiText('failedToSaveAddress', lang), { variant: "error" });
        } finally {
            setSaving(false);
        }
    }

    if (loading) return <BackdropLoader />;

    return (
        <>
            <MetaData title="Shipping Details | 1313 Mart" />
            {saving && <BackdropLoader />}
            <main className="w-full mt-20">

                <div className="flex flex-col sm:flex-row gap-3.5 w-full sm:w-11/12 mt-0 sm:mt-4 m-auto sm:mb-7 overflow-hidden">

                    <div className="flex-1">

                        <Stepper activeStep={1}>
                            <div className="w-full bg-white">

                                <form onSubmit={shippingSubmit} autoComplete="off" className="flex flex-col justify-start gap-3 w-full sm:w-3/4 mx-1 sm:mx-8 my-4">

                                    {addresses.length > 0 && !showForm && (
                                        <>
                                            <p className="font-medium text-gray-700">{uiText('chooseDeliveryAddress', lang)}</p>
                                            <RadioGroup value={selectedId} onChange={(e) => setSelectedId(e.target.value)}>
                                                {addresses.map((addr) => (
                                                    <label key={addr._id} className={`flex items-start gap-2 border rounded p-3 mb-2 cursor-pointer ${selectedId === addr._id ? "border-primary-blue bg-blue-50" : ""}`}>
                                                        <Radio value={addr._id} size="small" />
                                                        <div className="text-sm">
                                                            <p className="font-medium flex items-center gap-2">
                                                                {addr.label}
                                                                {addr.isDefault && <span className="text-xs bg-blue-100 text-primary-blue px-2 py-0.5 rounded-full">{uiText('defaultBadge', lang)}</span>}
                                                            </p>
                                                            <p className="text-gray-600">{addr.address}, {addr.area}, {addr.region}</p>
                                                            <p className="text-gray-500">Phone: +{addr.phoneCountryCode || '852'} {addr.phoneNo}</p>
                                                        </div>
                                                    </label>
                                                ))}
                                            </RadioGroup>
                                            <button type="button" onClick={() => setShowForm(true)} className="text-sm text-primary-blue hover:underline w-fit">
                                                {uiText('addNewAddressInstead', lang)}
                                            </button>
                                        </>
                                    )}

                                    {showForm && (
                                        <>
                                            {addresses.length > 0 && (
                                                <button type="button" onClick={() => setShowForm(false)} className="text-sm text-primary-blue hover:underline w-fit">
                                                    &larr; {uiText('chooseSavedAddressInstead', lang)}
                                                </button>
                                            )}

                                            <TextField
                                                value={form.address}
                                                onChange={(e) => setForm({ ...form, address: e.target.value })}
                                                fullWidth
                                                label={uiText('addressFieldLabel', lang)}
                                                variant="outlined"
                                                required
                                            />

                                            <div className="flex gap-6">
                                                <TextField
                                                    label={uiText('regionLabel', lang)}
                                                    select
                                                    fullWidth
                                                    variant="outlined"
                                                    value={form.region}
                                                    onChange={(e) => setForm({ ...form, region: e.target.value, areaId: "" })}
                                                    required
                                                >
                                                    {regions.map((r) => <MenuItem key={r} value={r}>{r}</MenuItem>)}
                                                </TextField>

                                                <TextField
                                                    label={uiText('areaLabel', lang)}
                                                    select
                                                    fullWidth
                                                    variant="outlined"
                                                    value={form.areaId}
                                                    onChange={(e) => setForm({ ...form, areaId: e.target.value })}
                                                    required
                                                    disabled={!form.region}
                                                >
                                                    {areasForRegion.map((a) => <MenuItem key={a._id} value={a._id}>{a.name}</MenuItem>)}
                                                </TextField>
                                            </div>

                                            {selectedArea && (
                                                <div className="bg-blue-50 rounded p-3 text-sm text-gray-700">
                                                    <p>{uiText('deliveryDaysLabel', lang)} {selectedArea.deliveryDays || "—"}</p>
                                                    <p>{uiText('freeShippingNote', lang)} HK${selectedArea.minOrderValue} ({uiText('otherwiseShippingNote', lang)} HK${selectedArea.shippingCharge} {uiText('shippingWord', lang)})</p>
                                                </div>
                                            )}

                                            <div className="flex gap-3">
                                                <TextField
                                                    select
                                                    label={uiText('codeLabel', lang)}
                                                    variant="outlined"
                                                    value={form.phoneCountryCode}
                                                    onChange={(e) => setForm({ ...form, phoneCountryCode: e.target.value })}
                                                    sx={{ minWidth: 190 }}
                                                >
                                                    {COUNTRY_CODES.map((c) => (
                                                        <MenuItem value={c.code} key={c.code}>{c.label}</MenuItem>
                                                    ))}
                                                </TextField>
                                                <TextField
                                                    value={form.phoneNo}
                                                    onChange={(e) => setForm({ ...form, phoneNo: e.target.value.replace(/\D/g, '') })}
                                                    type="tel"
                                                    label={uiText('phoneNoLabel', lang)}
                                                    fullWidth
                                                    variant="outlined"
                                                    required
                                                />
                                            </div>

                                            <FormControlLabel
                                                control={<Checkbox checked={saveThisAddress} onChange={(e) => setSaveThisAddress(e.target.checked)} />}
                                                label={uiText('saveAddressCheckbox', lang)}
                                            />
                                        </>
                                    )}

                                    <div className="flex items-center justify-between w-full my-2">
                                        <button type="button" onClick={() => navigate('/cart')} className="text-sm font-medium text-gray-600 hover:text-primary-blue flex items-center gap-1">
                                            &larr; {uiText('backToCart', lang)}
                                        </button>
                                        <button type="submit" className="bg-primary-orange py-3.5 px-8 text-sm font-medium text-white shadow hover:shadow-lg rounded-sm uppercase outline-none">
                                            {showForm ? uiText('saveAndDeliverHere', lang) : uiText('deliverHere', lang)}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </Stepper>
                    </div>

                    <PriceSidebar cartItems={cartItems} pricing={pricing} />
                </div>
            </main>
        </>
    );
};

export default Shipping;
